import * as React from 'react';
import { AlertCircle, CloudOff, Inbox, Lock } from 'lucide-react';
import { useT } from '@/i18n';
import { ApiError } from '@/lib/api';
import { useErrorText } from '@/hooks/useErrorText';
import { Button } from './ui/button';
import { Skeleton } from './ui/misc';
import { cn } from '@/lib/utils';

/**
 * Standard route states (spec §3: loading, empty, offline, recoverable error, unauthorized, success).
 * Every page uses these so behaviour is consistent.
 */
export function LoadingState({ rows = 3, className }: { rows?: number; className?: string }) {
  const t = useT();
  return (
    <div className={cn('space-y-3', className)} aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  );
}

function Shell({ icon, title, text, action, className }: { icon: React.ReactNode; title: string; text?: string; action?: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border-strong bg-surface/60 px-6 py-12 text-center', className)}>
      <div className="grid size-12 place-items-center rounded-full bg-paper-2 text-muted [&_svg]:size-6">{icon}</div>
      <div className="text-[15px] font-semibold">{title}</div>
      {text && <p className="max-w-md text-[13.5px] text-muted">{text}</p>}
      {action}
    </div>
  );
}

export function EmptyState({ title, text, action, icon, className }: { title?: string; text?: string; action?: React.ReactNode; icon?: React.ReactNode; className?: string }) {
  const t = useT();
  return <Shell icon={icon ?? <Inbox />} title={title ?? t('common.empty')} text={text} action={action} className={className} />;
}

export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  const t = useT();
  const errText = useErrorText();
  if (error instanceof ApiError && error.isNetwork) return <OfflineState onRetry={onRetry} className={className} />;
  if (error instanceof ApiError && (error.status === 403 || error.code === 'FORBIDDEN'))
    return <Shell icon={<Lock />} title={t('err.FORBIDDEN')} className={className} />;
  return (
    <Shell
      icon={<AlertCircle />}
      title={errText(error)}
      action={onRetry && <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button>}
      className={className}
    />
  );
}

export function OfflineState({ onRetry, className }: { onRetry?: () => void; className?: string }) {
  const t = useT();
  return (
    <Shell
      icon={<CloudOff />}
      title={t('common.offline')}
      text={t('err.network')}
      action={onRetry && <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button>}
      className={className}
    />
  );
}

/** Wrap a TanStack query result in the standard states. */
export function QueryState<T>({
  q,
  children,
  empty,
  isEmpty,
  rows,
}: {
  q: { data: T | undefined; error: unknown; isLoading: boolean; refetch: () => unknown };
  children: (d: T) => React.ReactNode;
  empty?: React.ReactNode;
  isEmpty?: (d: T) => boolean;
  rows?: number;
}) {
  if (q.isLoading) return <LoadingState rows={rows} />;
  if (q.error && q.data === undefined) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (q.data === undefined) return <LoadingState rows={rows} />;
  if (isEmpty?.(q.data)) return <>{empty ?? <EmptyState />}</>;
  return <>{children(q.data)}</>;
}
